import { splitEvery, times } from 'ramda';
import React from 'react';
import styled, { css } from 'styled-components';
import { media } from '@peloton/styles';
import { slate3 } from '@engage/colors';
import type { ClassPreview } from '@engage/library';
import { spaces } from '@engage/styles';
import { label12Caps } from '@engage/typography';
import { useAria } from '@members/copy';
import { Aria } from '@members/copy/models.generated/Aria';

import { SkipLinks as SkipLinksText } from '@members/copy/models.generated/SkipLinks';
import { SkipLinks, categoriesElementId } from '@members/layout';
import type { Props as ContainerProps } from './Classes';
import { ClassPreviewCard } from './ClassPreviewCard';
import { UpsellEncouragementCard } from './upsell-encouragement/UpsellEncouragementCard';

const splitClassesByRow = (columns: number, classes: ClassPreview[]) =>
  splitEvery(columns, classes);

const skipLinks = {
  first: {
    skipLinkId: SkipLinksText.BackToCategories,
    contentElementId: categoriesElementId,
  },
};

const StyledSkipLink = styled.a`
  ${label12Caps}
  color: ${slate3};
  font-weight: 400;
  position: absolute;
  left: -100vw;
  z-index: 100;

  &:focus {
    left: -${spaces.large}px;
    top: -${spaces.xSmall}px;
    position: relative;
    padding: ${spaces.xxSmall}px;
  }
`;

export const LibraryGrid: React.FC<React.PropsWithChildren<Props>> = ({
  classes,
  columns,
  onBookmarkClick,
}) => {
  const numberOfRowsInTwelveClasses = Math.floor(12 / columns);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const classAriaLabel = useAria(Aria.Class);
  return (
    <Grid>
      {splitClassesByRow(columns, classes).map((libraryRow, rowIndex) => {
        const shouldShowSkipLink = (rowIndex + 1) % numberOfRowsInTwelveClasses === 0;
        return (
          <div key={rowIndex}>
            <Row>
              {libraryRow.map((klass, position) =>
                klass?.description === 'upsell_class' ? (
                  <Cell ref={scrollRef} key={klass.id}>
                    <UpsellEncouragementCard
                      imageUrl={klass.imageUrl}
                      rideId={klass.id}
                      fitnessDiscipline={klass.fitnessDiscipline.name}
                      scrollRef={scrollRef}
                    ></UpsellEncouragementCard>
                  </Cell>
                ) : (
                  <Cell key={klass.id} role="group" aria-label={classAriaLabel}>
                    <ClassPreviewCard
                      onBookmarkClick={() =>
                        onBookmarkClick(klass.id, klass.isBookmarked)
                      }
                      listPosition={rowIndex * columns + position}
                      {...klass}
                    />
                  </Cell>
                ),
              )}
              {times(
                i => (
                  <Placeholder aria-hidden={true} key={`placeholder-${i}`} />
                ),
                columns - libraryRow.length,
              )}
            </Row>
            {shouldShowSkipLink ? (
              <SkipLinks skipLinks={skipLinks} StyledLink={StyledSkipLink} />
            ) : null}
          </div>
        );
      })}
    </Grid>
  );
};

const cellStyles = css`
  flex: 1;

  ${media.tabletLarge`

    * + & {
      margin-left: 10px;
    }
  `}

  ${media.desktop`
    * + & {
      margin-left: ${spaces.large}px;
    }
  `}
`;

const Placeholder = styled.div`
  ${cellStyles}
`;

const Cell = styled.div`
  position: relative;
  ${cellStyles}
`;

const Row = styled.div`
  display: flex;
  margin-bottom: 10px;

  ${media.desktop`
    margin-bottom: ${spaces.large}px;
  `}
`;

const Grid = styled.div`
  margin: auto;
  width: 100%;
`;

type Props = {
  columns: number;
} & Pick<ContainerProps, 'onBookmarkClick' | 'classes'>;
